/**
 * Lightweight change detector for collections using last_activity_at timestamps
 * 
 * Instead of fetching full collection data every 45 seconds, we can check if any
 * collection has changed by comparing the maximum last_activity_at timestamp.
 * This is much more efficient than fetching all collection data.
 * 
 * The backend updates last_activity_at whenever:
 * - Items are added/removed from a collection
 * - Items are checked/unchecked in a collection
 * - Any collection_items row is modified
 */

import { useCallback } from 'react';
import API_BASE_URL from '../config';

interface CollectionChangeCheck {
  hasChanges: boolean;
  latestActivity: string | null;
}

/**
 * Check if collections have changed since the last check
 * Returns true if any collection has been modified
 */
export const useCollectionChangeDetector = () => {
  const checkForChanges = useCallback(async (
    token: string,
    lastKnownActivity: string | null
  ): Promise<CollectionChangeCheck> => {
    try {
      // Fetch only the latest activity timestamp (very lightweight query)
      const response = await fetch(
        `${API_BASE_URL}/api/collections/activity-check`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        console.error('[CollectionChangeDetector] Failed to check activity:', response.status);
        return { hasChanges: false, latestActivity: lastKnownActivity };
      }

      const data = await response.json();
      const latestActivity = data.latest_activity;

      // If we have no previous timestamp, assume changes
      if (!lastKnownActivity) {
        return { hasChanges: true, latestActivity };
      }

      // Compare timestamps
      const hasChanges = latestActivity !== lastKnownActivity;
      
      if (hasChanges) {
        console.log('[CollectionChangeDetector] Changes detected:', {
          previous: lastKnownActivity,
          current: latestActivity,
        });
      }

      return { hasChanges, latestActivity };
    } catch (error) {
      console.error('[CollectionChangeDetector] Error checking for changes:', error);
      return { hasChanges: false, latestActivity: lastKnownActivity };
    }
  }, []);

  return { checkForChanges };
};

export default useCollectionChangeDetector;
